import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import '@maersk-global/mds-components-core/mc-button';
import '@maersk-global/mds-components-core/mc-input';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-popover';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-tooltip';
import { environment } from '../../../environments/environment';
import { AuthPolicies } from '../../auth/auth-policies';
import { downloadBlob, generateDateFilename, handleDownloadError } from '../../helper/download-helpers';
import { utils } from '../../helper/utils';
import { DownloadFilterCriteria, DownloadRequest, toFilter } from '../../models/download-filter.model';
import {
  PartManufacturer,
  PartManufacturerFilter,
  PartManufacturerOption,
  PartManufacturerRow,
} from '../../models/part-manufacturer.model';
import { PartManufacturerService } from '../../services/part-manufacturer.service';
import { ToastrService } from '../../services/toastr-service';
import { PaginationComponent } from '../../shared-components/aems-pagination/aems-pagination.component';
import { MasterDataAuditLogComponent } from '../../shared-components/master-data-audit-log/master-data-audit-log.component';
import { TagWithOverflowComponent } from '../../shared-components/tag-with-overflow/tag-with-overflow.component';

@Component({
  selector: 'app-oem-discount',
  imports: [
    CommonModule,
    DatePipe,
    ReactiveFormsModule,
    RouterLink,
    PaginationComponent,
    MasterDataAuditLogComponent,
    TagWithOverflowComponent,
  ],
  templateUrl: './oem-discount.component.html',
  styleUrls: ['./oem-discount.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class OemDiscountComponent implements OnInit {
  readonly filters = this.formBuilder.nonNullable.group({
    searchText: '',
    partManufacturerCodeList: this.formBuilder.nonNullable.control<string[]>([]),
    nameList: this.formBuilder.nonNullable.control<string[]>([]),
  });
  readonly form = this.formBuilder.nonNullable.group({
    partManufacturerCode: ['', [Validators.required, Validators.maxLength(3)]],
    name: ['', [Validators.required, Validators.maxLength(35)]],
    discountPercentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
  });
  readonly columns: Array<TableColumn> = [
    { id: 'partManufacturerCode', label: 'Manufacturer Code', width: '180px' },
    { id: 'name', label: 'Manufacturer Name', width: '280px' },
    { id: 'discountPercentage', label: 'Discount %', width: '130px' },
    { id: 'updatedBy', label: 'Updated By', width: '180px' },
    {
      id: 'updatedAt',
      label: 'Last Updated',
      width: '200px',
      dataType: {
        type: 'string',
        formatter: (value) => utils.dateFormatter.format(new Date(String(value))),
      },
    },
  ];
  readonly mode = signal<'list' | 'detail' | 'add' | 'edit'>('list');
  readonly detail = signal<PartManufacturer | null>(null);
  readonly error = signal('');
  readonly submitting = signal(false);
  readonly deleteOpen = signal(false);
  readonly canAdd =
    this.authPolicies.canAddOemDiscount &&
    environment.Features.EnableContractRatesAndDiscount &&
    environment.Features.EnableOemDiscountActions;
  readonly canEdit =
    this.authPolicies.canEditOemDiscount &&
    environment.Features.EnableContractRatesAndDiscount &&
    environment.Features.EnableOemDiscountActions;
  readonly canDelete =
    this.authPolicies.canDeleteOemDiscount &&
    environment.Features.EnableContractRatesAndDiscount &&
    environment.Features.EnableOemDiscountActions;
  readonly canViewAuditLog = environment.Features.EnableOemDiscountActions;
  readonly downloadColumns = ['part_manufacturer_code', 'name', 'discount_percentage'];
  readonly appliedFilterOverflowCount = 2;

  data: PartManufacturerRow[] = [];
  options: PartManufacturerOption[] = [];
  loading = false;
  filtersOpen = false;
  isDownload = false;
  auditOpen = false;
  currentPage = 1;
  pageLimit = 20;
  lastPage = 1;
  totalRecords = 0;
  sort: string | undefined;
  private code = '';
  private readonly baseRoute = '/master-data/shop-contract/oem-discount';

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly service: PartManufacturerService,
    private readonly toastr: ToastrService,
    public readonly authPolicies: AuthPolicies,
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(() => this.initialiseRouteMode());
  }

  load(): void {
    this.loading = true;
    this.service.getList(this.currentPage, this.pageLimit, this.sort, this.buildFilters()).subscribe({
      next: (response) => {
        this.data = response.body ?? [];
        const headers = response.headers ?? new HttpHeaders();
        this.currentPage = Number(headers.get('Current-Page') ?? headers.get('current-page') ?? this.currentPage);
        this.lastPage = Number(headers.get('Last-Page') ?? headers.get('last-page') ?? 1);
        this.totalRecords = Number(headers.get('Total-Item-Count') ?? headers.get('total-item-count') ?? this.data.length);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.data = [];
        this.loading = false;
        if (error.status !== 404) {
          this.toastr.showToastr('Unable to load OEM discounts', 'error');
        }
      },
    });
  }

  search(): void {
    this.currentPage = 1;
    this.load();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.filtersOpen = false;
    this.load();
  }

  clearFilters(): void {
    this.filters.reset({ searchText: '', partManufacturerCodeList: [], nameList: [] });
    this.applyFilters();
  }

  get hasAppliedFilters(): boolean {
    return (
      this.filters.controls.partManufacturerCodeList.value.length > 0 ||
      this.filters.controls.nameList.value.length > 0 ||
      !!this.filters.controls.searchText.value
    );
  }

  removePartManufacturerCodeFilter(): (value: string) => void {
    return (value: string) => this.removeFilterValue('partManufacturerCodeList', value);
  }

  removeManufacturerNameFilter(): (value: string) => void {
    return (value: string) => this.removeFilterValue('nameList', value);
  }

  addNew(): void {
    this.router.navigate([this.baseRoute, 'add-new']);
  }

  edit(): void {
    if (this.detail()) {
      this.router.navigate([this.baseRoute, this.code, 'edit']);
    }
  }

  onSort(event: Event): void {
    const detail = (event as CustomEvent<{ column?: string; id?: string; direction?: string }>).detail;
    const column = detail?.column ?? detail?.id;
    if (!column || !detail.direction) return;
    this.sort = `${column}:${detail.direction === 'ascending' ? 'asc' : 'desc'}`;
    this.currentPage = 1;
    this.load();
  }

  pageChanged(page: number): void {
    this.currentPage = page;
    this.load();
  }

  pageSizeChanged(limit: number): void {
    this.pageLimit = limit;
    this.currentPage = 1;
    this.load();
  }

  onDownload(): void {
    this.isDownload = true;
    const request: DownloadRequest = {
      columns: this.downloadColumns,
      filters: this.buildDownloadFilters(),
    };
    this.service.downloadToExcel(request).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('oem-discount'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastr);
        this.isDownload = false;
      },
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    const value = this.form.getRawValue();
    const operation =
      this.mode() === 'edit'
        ? this.service.update(this.code, { name: value.name.trim(), discountPercentage: value.discountPercentage })
        : this.service.create({
            partManufacturerCode: value.partManufacturerCode.trim().toUpperCase(),
            name: value.name.trim(),
            discountPercentage: value.discountPercentage,
          });

    operation.subscribe({
      next: (manufacturer) => {
        this.toastr.showToastr(`OEM discount ${this.mode() === 'edit' ? 'updated' : 'added'} successfully`, 'success');
        this.router.navigate([this.baseRoute, manufacturer.partManufacturerCode]);
      },
      error: (error: HttpErrorResponse) => {
        this.submitting.set(false);
        this.toastr.showToastr(error.error?.message ?? 'Failed to save OEM discount', 'error');
      },
    });
  }

  delete(): void {
    this.deleteOpen.set(false);
    this.submitting.set(true);
    this.service.delete(this.code).subscribe({
      next: () => {
        this.toastr.showToastr('OEM discount deleted successfully', 'success');
        this.router.navigate([this.baseRoute]);
      },
      error: () => {
        this.submitting.set(false);
        this.toastr.showToastr('Failed to delete OEM discount', 'error');
      },
    });
  }

  cancel(): void {
    const route = this.mode() === 'edit' ? [this.baseRoute, this.code] : [this.baseRoute];
    this.router.navigate(route);
  }

  readonly auditLogFetcher = (_transactionCode: string, isSystemLog: boolean, page: number, limit: number) =>
    this.service.getAuditLogs(this.code, isSystemLog, page, limit);

  private initialiseRouteMode(): void {
    const url = this.router.url;
    this.code = this.route.snapshot.paramMap.get('partManufacturerCode') ?? '';
    this.error.set('');
    this.detail.set(null);
    this.deleteOpen.set(false);
    this.auditOpen = false;
    this.submitting.set(false);

    if (url.endsWith('/add-new')) {
      this.mode.set('add');
      this.form.reset({ partManufacturerCode: '', name: '', discountPercentage: 0 });
      this.form.controls.partManufacturerCode.enable();
      return;
    }

    if (url.endsWith('/edit')) {
      this.mode.set('edit');
      this.loadFormDetail();
      return;
    }

    if (this.code) {
      this.mode.set('detail');
      this.loadDetail();
      return;
    }

    this.mode.set('list');
    this.service.getAll().subscribe({
      next: (options) => (this.options = options),
      error: () => this.toastr.showToastr('Unable to load OEM discount filters', 'error'),
    });
    this.load();
  }

  private loadDetail(): void {
    if (!this.code) {
      this.error.set('No manufacturer code was provided.');
      return;
    }
    this.service.getByCode(this.code).subscribe({
      next: (detail) => this.detail.set(detail),
      error: () => this.error.set('OEM discount not found.'),
    });
  }

  private loadFormDetail(): void {
    if (!this.code) return;
    this.service.getByCode(this.code).subscribe({
      next: (manufacturer) => {
        this.form.setValue({
          partManufacturerCode: manufacturer.partManufacturerCode,
          name: manufacturer.name,
          discountPercentage: manufacturer.discountPercentage,
        });
        this.form.controls.partManufacturerCode.disable();
      },
      error: () => {
        this.toastr.showToastr('OEM discount not found', 'error');
        this.router.navigate([this.baseRoute]);
      },
    });
  }

  private buildFilters(): PartManufacturerFilter {
    const value = this.filters.getRawValue();
    return {
      searchText: value.searchText || undefined,
      partManufacturerCodeList: value.partManufacturerCodeList.length ? value.partManufacturerCodeList : undefined,
      nameList: value.nameList.length ? value.nameList : undefined,
    };
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const value = this.filters.getRawValue();
    return [
      ...(value.partManufacturerCodeList.length ? [toFilter('part_manufacturer_code', value.partManufacturerCodeList)] : []),
      ...(value.nameList.length ? [toFilter('name', value.nameList)] : []),
    ];
  }

  private removeFilterValue(controlName: 'partManufacturerCodeList' | 'nameList', valueToRemove: string): void {
    const values = this.filters.controls[controlName].value.filter((value) => value !== valueToRemove);
    this.filters.controls[controlName].setValue(values);
    this.applyFilters();
  }
}
